/**
 * JobDetailsPage
 *
 * Features:
 * - Glassmorphism cards with gradient accents
 * - 2-column layout (media + results)
 * - Clean, minimal header
 * - Lucide icons throughout
 * - Job-type specific result rendering
 */
import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Download,
  Trash2,
  Clock,
  Calendar,
  Image,
  Video,
  RefreshCw,
  AlertCircle,
  CheckCircle2,
  Loader2,
  FileWarning,
  Camera,
  Box,
} from "lucide-react";
import { Button, Spinner, EmptyState } from "../components/common";
import { ConfirmationModal } from "../components/common/ConfirmationModal";
import { JobStatusBadge } from "../components/jobs/JobStatusBadge";
import {
  PeopleCountingResults,
  PlateDetectionResults,
  ParkingAnalysisResults,
  InOutCountingResults,
  PotholeDetectionResults,
  FoodWasteResults,
  RoomReadinessResults,
  LobbyAlertResults,
  WildlifeDetectionResults,
  DamageDetection3DResults,
  MultiCameraResults,
  generateMockMultiCameraResults,
  PPEDetectionResults,
} from "../components/results";

import { useAppDispatch, useAppSelector, jobsActions } from "../redux";
import {
  JobType,
  DamageDetection3DResults as DamageDetection3DResultsType,
  MultiCameraResults as MultiCameraResultsType,
  PPEDetectionResults as PPEDetectionResultsType,
} from "../types";

import { ROUTES } from "../services/routes";
import { JOB_TYPE_CONFIGS } from "../services/jobTypes";
import { getJobIcon } from "../utils/icons";
import {
  formatDate,
  formatJobType,
  formatJobNumber,
  getFileUrl,
  downloadFile,
} from "../utils";

export const JobDetailsPage: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { jobs, loading } = useAppSelector((state) => state.jobs);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const job = jobs.find((j: any) => j.id === jobId);
  const jobConfig = job ? JOB_TYPE_CONFIGS[job.job_type as JobType] : null;

  useEffect(() => {
    if (jobId) {
      dispatch(jobsActions.fetchJobById(jobId));
    }
  }, [jobId, dispatch]);

  // Auto-refresh for processing jobs
  useEffect(() => {
    if (!job || !autoRefresh) return;
    if (job.status === "processing" || job.status === "pending") {
      const interval = setInterval(() => {
        dispatch(jobsActions.fetchJobById(jobId!));
      }, 3000);
      return () => clearInterval(interval);
    }
  }, [job, jobId, autoRefresh, dispatch]);

  const isLiveDemoJob = () => {
    if (!job) return false;
    return job.is_live_demo === true || (job as any).isLiveDemo === true;
  };

  const handleDownload = () => {
    if (!job?.annotated_output_url) return;
    try {
      const isWebm =
        job.input_file_path?.match(/\.webm$/i) ||
        job.annotated_output_path?.match(/\.webm$/i);
      const isVideo =
        job.input_file_path?.match(/\.(mp4|avi|mov|webm)$/i) ||
        job.annotated_output_path?.match(/\.(mp4|avi|mov|webm)$/i) ||
        isLiveDemoJob();

      let extension = "png";
      if (isWebm) extension = "webm";
      else if (isVideo) extension = "mp4";

      downloadFile(
        job.annotated_output_url,
        `job_${job.job_number}_annotated.${extension}`
      );
    } catch (error) {
      console.error("Error downloading file:", error);
      alert("Failed to download file. Please try again.");
    }
  };

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // ... (existing code)

  const handleDelete = async () => {
    if (!jobId) return;
    try {
      setIsDeleting(true);
      await dispatch(jobsActions.deleteJob(jobId)).unwrap();
      navigate(ROUTES.JOBS);
    } catch (error) {
      console.error("Failed to delete job:", error);
      setIsDeleting(false);
      setIsDeleteModalOpen(false);
    }
  };

  // Get live demo results
  const getLiveDemoResults = () => {
    if (!isLiveDemoJob()) return null;
    if (job.results?.session_summary) {
      return {
        ...job.results,
        average_count: job.results.session_summary.avg_count,
        max_count: job.results.session_summary.max_count,
        min_count: job.results.session_summary.min_count,
        total_frames: job.results.session_summary.total_frames,
        duration: job.results.session_summary.duration_seconds,
      };
    }
    if (job.config?.session_data) {
      const sessionData = job.config.session_data;
      return {
        success: true,
        type: "video",
        is_live_demo: true,
        average_count: sessionData.statistics?.avgCount || 0,
        max_count: sessionData.statistics?.maxCount || 0,
        min_count: sessionData.statistics?.minCount || 0,
        total_frames: sessionData.totalFrames || 0,
        duration: sessionData.sessionDuration || 0,
        frame_results: sessionData.analysisResults || [],
      };
    }
    return job.results;
  };

  // Render results based on job type
  const renderResults = () => {
    const isLive = isLiveDemoJob();
    const resultsToUse = isLive ? getLiveDemoResults() : job?.results;

    if (!resultsToUse) {
      if (job?.job_type === JobType.MULTI_CAMERA_SYSTEM) {
        const mockResults = generateMockMultiCameraResults(
          job.config?.cameras?.length || 3
        );
        return <MultiCameraResults results={mockResults} />;
      }
      return (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <Loader2 className="w-12 h-12 text-primary-400 animate-spin mb-4" />
          <h3 className="text-lg font-medium text-white mb-2">Processing...</h3>
          <p className="text-slate-400 text-sm">
            Your analysis is being processed. Results will appear shortly.
          </p>
        </div>
      );
    }

    if (isLive) {
      return <LiveDemoResults results={resultsToUse} jobType={job.job_type} />;
    }

    switch (job.job_type) {
      case JobType.PEOPLE_COUNTING:
        return <PeopleCountingResults results={resultsToUse as any} />;
      case JobType.NUMBER_PLATE_DETECTION:
        return <PlateDetectionResults results={resultsToUse as any} />;
      case JobType.PARKING_ANALYSIS:
        return <ParkingAnalysisResults results={resultsToUse as any} />;
      case JobType.IN_OUT_COUNTING:
        return <InOutCountingResults results={resultsToUse as any} />;
      case JobType.POTHOLE_DETECTION:
        return <PotholeDetectionResults results={resultsToUse as any} />;
      case JobType.FOOD_WASTE_ESTIMATION:
        return <FoodWasteResults results={resultsToUse as any} />;
      case JobType.ROOM_READINESS:
        return <RoomReadinessResults results={resultsToUse as any} />;
      case JobType.LOBBY_CROWD_DETECTION:
        return <LobbyAlertResults results={resultsToUse as any} />;
      case JobType.WILDLIFE_DETECTION:
        return <WildlifeDetectionResults results={resultsToUse as any} />;
      case JobType.DAMAGE_DETECTION_3D:
        return (
          <DamageDetection3DResults
            results={resultsToUse as DamageDetection3DResultsType}
            annotatedOutputUrl={job.annotated_output_url || undefined}
            inputFileName={job.input_file_path?.split("/").pop()}
          />
        );
      case JobType.PPE_DETECTION:
        return (
          <PPEDetectionResults
            results={resultsToUse as PPEDetectionResultsType}
            annotatedOutputUrl={job.annotated_output_url || undefined}
            inputFileName={job.input_file_path?.split("/").pop()}
          />
        );
      case JobType.MULTI_CAMERA_SYSTEM:
        return (
          <MultiCameraResults
            results={resultsToUse as MultiCameraResultsType}
          />
        );
      default:
        return (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <FileWarning className="w-12 h-12 text-slate-500 mb-4" />
            <h3 className="text-lg font-medium text-white mb-2">
              Unknown Job Type
            </h3>
            <p className="text-slate-400 text-sm">
              Unable to display results for this job type.
            </p>
          </div>
        );
    }
  };

  // Loading state
  if (loading && !job) {
    return (
      <div className="min-h-screen bg-navy-900 flex items-center justify-center">
        <div className="text-center">
          <Spinner size="xl" />
          <p className="text-slate-400 mt-4">Loading job details...</p>
        </div>
      </div>
    );
  }

  // Not found state
  if (!job) {
    return (
      <div className="min-h-screen bg-navy-900 flex items-center justify-center">
        <EmptyState
          icon="❌"
          title="Job Not Found"
          description="The requested job could not be found."
          action={{
            label: "Back to Jobs",
            onClick: () => navigate(ROUTES.JOBS),
          }}
        />
      </div>
    );
  }

  // Determine media type
  const isWebm =
    job.input_file_path?.match(/\.webm$/i) ||
    job.annotated_output_path?.match(/\.webm$/i);
  const isVideo =
    job.input_file_path?.match(/\.(mp4|avi|mov|webm)$/i) ||
    job.annotated_output_path?.match(/\.(mp4|avi|mov|webm)$/i) ||
    isLiveDemoJob();
  const is3DModel = job.input_file_path?.match(/\.(stl|obj|ply|step|stp)$/i);
  const isConfigBased = job.job_type === JobType.MULTI_CAMERA_SYSTEM;
  const mediaUrl = getFileUrl(job.annotated_output_url);
  const isDamageDetection = job.job_type === JobType.DAMAGE_DETECTION_3D;
  const isMultiCamera = job.job_type === JobType.MULTI_CAMERA_SYSTEM;

  const getInputTypeDisplay = () => {
    if (isConfigBased) return `${job.config?.cameras?.length || 0} Cameras`;
    if (is3DModel) return "3D Model";
    if (isLiveDemoJob()) return "Live Demo";
    if (isVideo) return "Video";
    return "Image";
  };

  const getStatusIcon = () => {
    switch (job.status) {
      case "completed":
        return <CheckCircle2 className="w-5 h-5 text-emerald-400" />;
      case "processing":
        return <Loader2 className="w-5 h-5 text-amber-400 animate-spin" />;
      case "failed":
        return <AlertCircle className="w-5 h-5 text-red-400" />;
      default:
        return <Clock className="w-5 h-5 text-slate-400" />;
    }
  };

  const getInputIcon = () => {
    if (isConfigBased) return <Camera className="w-4 h-4" />;
    if (is3DModel) return <Box className="w-4 h-4" />;
    if (isVideo) return <Video className="w-4 h-4" />;
    return <Image className="w-4 h-4" />;
  };

  return (
    <div className="min-h-screen bg-navy-900">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Top Bar */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate(ROUTES.JOBS)}
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors group"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            <span className="font-medium">Back to Jobs</span>
          </button>

          <div className="flex items-center gap-3">
            {job.status === "completed" &&
              !isDamageDetection &&
              !isMultiCamera &&
              job.annotated_output_url && (
                <Button
                  variant="primary"
                  onClick={handleDownload}
                  icon={<Download className="w-4 h-4" />}
                >
                  Download
                </Button>
              )}
            <Button
              variant="danger"
              onClick={() => setIsDeleteModalOpen(true)}
              icon={<Trash2 className="w-4 h-4" />}
            >
              Delete
            </Button>
          </div>
        </div>

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          isOpen={isDeleteModalOpen}
          onClose={() => setIsDeleteModalOpen(false)}
          onConfirm={handleDelete}
          title="Delete Job"
          message={`Are you sure you want to delete job ${job.job_number}? This action cannot be undone.`}
          confirmLabel="Delete"
          isLoading={isDeleting}
        />

        {/* Job Header Card */}
        <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl p-6 mb-6">
          <div className="flex flex-col lg:flex-row lg:items-center gap-6">
            {/* Job Info */}
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3 flex-wrap">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500/20 to-accent-500/20 border border-primary-500/30 flex items-center justify-center">
                  {jobConfig ? getJobIcon(jobConfig.iconName, "w-6 h-6") : "📊"}
                </div>
                <div>
                  <h1 className="text-xl font-bold text-white">
                    {formatJobType(job.job_type)}
                  </h1>
                  <p className="text-slate-400 text-sm font-mono">
                    {formatJobNumber(job.job_number)}
                  </p>
                </div>
                <div className="flex items-center gap-2 ml-auto lg:ml-4">
                  <JobStatusBadge status={job.status} />
                  {isLiveDemoJob() && (
                    <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-purple-500/20 text-purple-400 border border-purple-500/30">
                      Live Demo
                    </span>
                  )}
                  {jobConfig?.category === "advanced" && (
                    <span className="px-2.5 py-1 text-xs font-medium rounded-full bg-violet-500/20 text-violet-400 border border-violet-500/30">
                      Advanced
                    </span>
                  )}
                </div>
              </div>

              {/* Metadata Grid */}
              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
                <div className="flex items-center gap-2 text-slate-400">
                  <Calendar className="w-4 h-4" />
                  <span>
                    {formatDate(job.submitted_at, "MMM dd, yyyy • HH:mm")}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-slate-400">
                  {getInputIcon()}
                  <span>{getInputTypeDisplay()}</span>
                </div>
                {job.completed_at && (
                  <div className="flex items-center gap-2 text-slate-400">
                    <Clock className="w-4 h-4" />
                    <span>
                      Completed {formatDate(job.completed_at, "HH:mm")}
                    </span>
                  </div>
                )}
                <label className="flex items-center gap-2 text-slate-400 cursor-pointer ml-auto">
                  <RefreshCw
                    className={`w-4 h-4 ${
                      autoRefresh ? "text-primary-400" : ""
                    }`}
                  />
                  <span className="text-xs">Auto-refresh</span>
                  <input
                    type="checkbox"
                    checked={autoRefresh}
                    onChange={(e) => setAutoRefresh(e.target.checked)}
                    className="sr-only"
                  />
                  <div
                    className={`w-8 h-4 rounded-full transition-colors ${
                      autoRefresh ? "bg-primary-500" : "bg-slate-700"
                    }`}
                  >
                    <div
                      className={`w-3 h-3 rounded-full bg-white transform transition-transform mt-0.5 ${
                        autoRefresh ? "translate-x-4 ml-0.5" : "translate-x-0.5"
                      }`}
                    />
                  </div>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {job.error_message && (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-6 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-red-400 font-medium mb-1">Error</h3>
              <p className="text-red-300 text-sm">{job.error_message}</p>
            </div>
          </div>
        )}

        {/* Main Content - 2 Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Media Panel */}
          {mediaUrl && !isDamageDetection && !isMultiCamera && (
            <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-700/50">
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  {job.status === "completed" ? (
                    <>
                      <CheckCircle2 className="w-5 h-5 text-emerald-400" />
                      Annotated Output
                    </>
                  ) : (
                    <>
                      {getInputIcon()}
                      Input Media
                    </>
                  )}
                </h2>
              </div>
              <div
                className="relative bg-slate-900"
                style={{ paddingTop: "56.25%" }}
              >
                {isVideo ? (
                  <video
                    controls
                    className="absolute top-0 left-0 w-full h-full object-contain"
                  >
                    <source src={mediaUrl} type="video/webm" />
                    <source src={mediaUrl} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                ) : (
                  <img
                    src={mediaUrl}
                    alt="Job output"
                    className="absolute top-0 left-0 w-full h-full object-contain"
                  />
                )}
              </div>
            </div>
          )}

          {/* Right: Results Panel */}
          <div
            className={`${
              mediaUrl && !isDamageDetection && !isMultiCamera
                ? ""
                : "lg:col-span-2"
            }`}
          >
            <div className="bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl overflow-hidden">
              <div className="px-5 py-4 border-b border-slate-700/50">
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  {getStatusIcon()}
                  {isMultiCamera ? "Camera Feeds" : "Analysis Results"}
                </h2>
              </div>
              <div className="p-5">{renderResults()}</div>
            </div>
          </div>
        </div>

        {/* Camera Configuration (for multi-camera jobs) */}
        {isMultiCamera && job.config?.cameras && (
          <div className="mt-6 bg-slate-800/50 backdrop-blur-sm border border-slate-700/50 rounded-2xl overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-700/50">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Camera className="w-5 h-5 text-primary-400" />
                Camera Configuration
              </h2>
            </div>
            <div className="p-5">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {job.config.cameras.map((camera: any, index: number) => (
                  <div
                    key={camera.id}
                    className="bg-slate-900/50 rounded-xl p-4 border border-slate-700/50"
                  >
                    <div className="flex items-center gap-2 mb-3">
                      <div className="w-8 h-8 rounded-lg bg-primary-500/20 flex items-center justify-center">
                        <Camera className="w-4 h-4 text-primary-400" />
                      </div>
                      <span className="font-medium text-white">
                        Camera {index + 1}
                      </span>
                    </div>
                    <div className="space-y-1 text-sm">
                      <p className="text-slate-400">
                        IP:{" "}
                        <span className="text-white font-mono">
                          {camera.ip}
                        </span>
                      </p>
                      <p className="text-slate-400">
                        Location:{" "}
                        <span className="text-white">{camera.location}</span>
                      </p>
                      {camera.description && (
                        <p className="text-slate-500 text-xs">
                          {camera.description}
                        </p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ============================================================================
// Live Demo Results Component
// ============================================================================
interface LiveDemoResultsProps {
  results: any;
  jobType: string;
}

const LiveDemoResults: React.FC<LiveDemoResultsProps> = ({
  results,
  jobType,
}) => {
  const avgCount =
    results?.average_count ?? results?.session_summary?.avg_count ?? 0;
  const maxCount =
    results?.max_count ?? results?.session_summary?.max_count ?? 0;
  const minCount =
    results?.min_count ?? results?.session_summary?.min_count ?? 0;
  const totalFrames =
    results?.total_frames ?? results?.session_summary?.total_frames ?? 0;
  const duration =
    results?.duration ?? results?.session_summary?.duration_seconds ?? 0;

  const getJobTypeLabel = () => {
    switch (jobType) {
      case "people_counting":
        return "People Counted";
      case "number_plate_detection":
        return "Plates Detected";
      case "parking_analysis":
        return "Vehicles Detected";
      case "food_waste_estimation":
        return "Items Detected";
      default:
        return "Objects Detected";
    }
  };

  const stats = [
    {
      label: "Average",
      value: typeof avgCount === "number" ? avgCount.toFixed(1) : avgCount,
      color: "text-primary-400",
    },
    { label: "Maximum", value: maxCount, color: "text-emerald-400" },
    {
      label: "Minimum",
      value: minCount === Infinity ? 0 : minCount,
      color: "text-amber-400",
    },
    { label: "Frames", value: totalFrames, color: "text-purple-400" },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-purple-500/20 border border-purple-500/30 flex items-center justify-center">
          <Video className="w-5 h-5 text-purple-400" />
        </div>
        <div>
          <h3 className="text-lg font-semibold text-white">
            {getJobTypeLabel()}
          </h3>
          <p className="text-slate-400 text-sm">Live Demo Session Analysis</p>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {stats.map((stat, idx) => (
          <div
            key={idx}
            className="bg-slate-900/50 rounded-xl p-4 border border-slate-700/50 text-center"
          >
            <p className={`text-3xl font-bold ${stat.color}`}>{stat.value}</p>
            <p className="text-slate-400 text-xs mt-1">{stat.label}</p>
          </div>
        ))}
      </div>

      {/* Session Info */}
      <div className="grid grid-cols-3 gap-4 text-sm pt-4 border-t border-slate-700/50">
        <div>
          <p className="text-slate-400">Duration</p>
          <p className="text-white font-medium">{duration}s</p>
        </div>
        <div>
          <p className="text-slate-400">Analysis Rate</p>
          <p className="text-white font-medium">~1 FPS</p>
        </div>
        <div>
          <p className="text-slate-400">Format</p>
          <p className="text-white font-medium">WebM (VP8)</p>
        </div>
      </div>
    </div>
  );
};
